import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { DEFAULT_LOCALE, SUPPORTED_CODES } from "./locales";
import enCommon from "../locales/en/common.json";

export const NAMESPACES = ["common"] as const;
export type Namespace = (typeof NAMESPACES)[number];

// Lazy bundles for every locale/namespace under src/locales, except the
// statically-imported English base. Adding a locale folder is picked up here
// automatically — no code change required.
const localeModules = import.meta.glob("../locales/*/*.json");

const STORAGE_KEY = "vocaler-locale";

function detectInitialLocale(): string {
  const stored = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  const envDefault = import.meta.env.VITE_DEFAULT_LOCALE;
  const candidate = stored || envDefault || DEFAULT_LOCALE;
  return SUPPORTED_CODES.includes(candidate) ? candidate : DEFAULT_LOCALE;
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { common: enCommon },
  },
  lng: detectInitialLocale(),
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: SUPPORTED_CODES,
  ns: NAMESPACES as unknown as string[],
  defaultNS: "common",
  interpolation: { escapeValue: false },
  returnNull: false,
});

/** Lazy-load and register all namespaces for a locale, then switch to it. */
export async function loadAndSetLocale(code: string): Promise<void> {
  if (!i18n.hasResourceBundle(code, "common")) {
    await Promise.all(
      NAMESPACES.map(async (ns) => {
        const path = `../locales/${code}/${ns}.json`;
        const loader = localeModules[path];
        if (!loader) return;
        const mod = (await loader()) as { default: Record<string, unknown> };
        i18n.addResourceBundle(code, ns, mod.default, true, true);
      }),
    );
  }
  await i18n.changeLanguage(code);
  if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, code);
}

export default i18n;
